// WAAP service gating — decides when the standalone WAAP console is offered.
//
// WAAP is its OWN service, fully separate from the CDN (Multi-CDN) console: the
// backend provisions it on the dedicated `cobra` provider (Conversant WAAP),
// with its own upstream entity and domains — sharing only the vendor SDK with
// the `eagle` CDN provider. So a service is WAAP iff its provider is `cobra`.
// (Previously WAAP rode `eagle`; that heuristic is obsolete — eagle is CDN-only
// now, cobra is WAAP.)
import type { AvailableService } from '@/api/services';

/** Provider codename that backs WAAP (Conversant WAAP — the Cobra provider). */
export const WAAP_PROVIDER = 'cobra';

/** True when a provisioned service can open the WAAP console. */
export function isWaapCapable(service: AvailableService): boolean {
  return service.provider?.name?.trim().toLowerCase() === WAAP_PROVIDER;
}

/** Route to a service's WAAP console. */
export function waapConsoleRoute(serviceId: string): string {
  return `/services/waap/${serviceId}`;
}

/** Card metadata for a WAAP service (mirrors the catalog `ServiceProduct` shape
 *  consumed by the services dashboard card). */
export const WAAP_PRODUCT = {
  displayName: 'WAAP',
  /** Full product name used as the created service's name on self-serve activation. */
  serviceName: 'Web Application & API Protection',
  description:
    'Web Application & API Protection — AI-WAF, bot management, rate limiting and API security for your websites.',
  features: ['AI-WAF', 'Bot Management', 'Rate Limiting', 'API Protection'],
} as const;

/**
 * WAAP card subtitle — the service identity WITHOUT the CDN provider branding.
 * WAAP is a separate product, but the backend names eagle services
 * "Eagle CDN - <entity> (<label>)"; strip the "Eagle CDN" prefix so the WAAP card
 * doesn't read as a CDN service. (Domains still bind via domains_services — only
 * the display label changes here.)
 */
export function waapServiceLabel(service: AvailableService): string {
  const name = (service.service_name || '').trim();
  const prov = (service.provider?.display_name || '').trim();
  if (prov && name.toLowerCase().startsWith(prov.toLowerCase())) {
    const rest = name.slice(prov.length).replace(/^\s*[-–—:]\s*/, '').trim();
    if (rest) return rest;
  }
  return name;
}
